import React, {type ReactNode, useState} from 'react';

import {DashboardChatWidget} from 'app/chat-app/components/DashboardChatWidget';
import TabbedModal from 'app/components/general/TabbedModal';
import {AddDocumentToFolder} from 'app/documents/components/DocumentsDashboard/AddDocumentToFolder';
import {DocumentsAddButton} from 'app/documents/components/DocumentsDashboard/DocumentsAddButton';
import {DashboardLocationParamsType} from 'app/documents/types';
import {useFolderByHashid} from 'app/folders/api';
import {
  BreadcrumbItemStructure,
  type BreadcrumbProps,
} from 'app/general/structures/BreadcrumbItemStructure';
import {SPPage} from 'app/general/structures/SPPage/SPPage';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {getDocumentsQRCodeURL, HOME_REDIRECT} from 'app/urls';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import {DocumentActionRequiredList} from './DocumentActionRequiredList';
import {FolderContext} from './FolderContext';
import {RecentCommentsWidget} from './RecentComments';
import {RecentTasksWidget} from './RecentTasks';

import type {WithRouterProps} from 'react-router';

interface Props
  extends WithRouterProps<
    {name: string; hashid: string},
    DashboardLocationParamsType
  > {
  children: ReactNode;
}

export function HomeDashboardFrame({children, ...props}: Props) {
  const folderHashid = props.params.hashid;
  const {currentData: currentFolder, refetch} = useFolderByHashid(
    folderHashid,
    {
      skip: folderHashid == null,
    }
  );

  const [addingItem, setAddingItem] = useState<boolean>(false);

  return (
    <FolderContext.Provider value={{folder: currentFolder, refetch: refetch}}>
      <SPPage
        {...props}
        breadcrumbProps={{params: props.params}}
        actionArea={({isTeamMember}) => (
          <>
            <div className="d-flex align-items-center">
              <ActionsButton
                actions={[
                  {
                    text: <a>{gt('Show QR Codes')}</a>,
                    icon: 'fa fa-qrcode',
                    key: 'qrcodes',
                    condition: () => folderHashid != null,
                    callback: () =>
                      window.open(getDocumentsQRCodeURL(folderHashid)),
                  },
                ]}
              />
              <DocumentsAddButton
                selectedFolderHashid={folderHashid}
                invisible={false}
                onAddExistingDocumentClick={() => setAddingItem(true)}
                onNewFolderClick={() => null}
                canAddFolder={false}
                canCreateDocument={currentFolder?.can_edit || isTeamMember}
                canAddExistingDocument={currentFolder?.can_edit || false}
              />
            </div>
            <TabbedModal
              show={addingItem}
              onCancel={() => setAddingItem(false)}
              tabs={[
                {
                  eventKey: 'add-existing-doc',
                  name: gt('Add Existing Document to Folder'),
                  icon: 'far fa-folder',
                  content: (
                    <AddDocumentToFolder
                      folderHashid={folderHashid}
                      finalCallback={() => setAddingItem(false)}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      >
        {children}
        <DashboardChatWidget />
      </SPPage>
    </FolderContext.Provider>
  );
}

export function HomeDashboard() {
  return (
    <div className="card-columns py-4">
      <RecentCommentsWidget />
      <DocumentActionRequiredList
        icon="far fa-certificate text-muted"
        title="Needs Review"
        filter="needs_review"
        sortBy={SORT_BY_TYPES.REVIEWED_DATE_NEXT_ASC}
      />

      <DocumentActionRequiredList
        icon="far fa-thumbs-up text-muted"
        title="Needs My Approval"
        filter="needs_my_approval"
        sortBy={SORT_BY_TYPES.APPROVED_DATE_DESC}
      />

      <DocumentActionRequiredList
        icon="far fa-signature text-muted"
        title="Needs My Sign Off"
        filter="signoff"
        sortBy={SORT_BY_TYPES.LAST_REQUEST_SENT_DATE_DESC}
      />
      <RecentTasksWidget />
    </div>
  );
}

export function HomeDashboardBreadcrumb(props: BreadcrumbProps) {
  return (
    <BreadcrumbItemStructure
      {...props}
      icon="fa-home"
      text={gt('Home')}
      active={props.active}
      link={HOME_REDIRECT}
    />
  );
}
